import { alpha, createTheme, Shadows } from "@mui/material/styles";
import { lightTheme } from "./theme";
import type { BrandTokens } from "../brandTokens";

/**
 * TeleHeadache light mode, from the "TeleHeadache Portal Brand & UI" guide (v1.0, 2026-10-11).
 *
 * Its own theme rather than edits to `theme.ts`'s shared pieces, because `darkTheme` spreads those.
 * Components never read the guide's values directly: they read `theme.palette.brand` (see
 * ../brandTokens.ts), which every theme defines, and this theme fills it from the guide.
 */
export interface ThGuideColours {
  ink: string;
  inkHover: string;
  teal: string;
  tealDark: string;
  brightTeal: string;
  clinicalBlue: string;
  slate: string;
  canvas: string;
  panelBlue: string;
  panelMint: string;
  border: string;
  inputBorder: string;
  shadow: string;
  status: BrandTokens["status"];
}

export const TH_TOKENS: ThGuideColours = {
  ink: "#0B1736",
  inkHover: "#16395B",
  teal: "#087D82",
  tealDark: "#055B61",
  brightTeal: "#56D3CB",
  clinicalBlue: "#2F6DE0",
  slate: "#4E5C73",
  canvas: "#FAFBFD",
  panelBlue: "#D7E6F4",
  panelMint: "#DDEFE9",
  border: "#E1E7EF",
  inputBorder: "#647087",
  shadow: "0 4px 14px rgb(11 23 54 / 4%)",
  status: {
    complete: { text: "#137A53", background: "#DCF4E8" },
    action: { text: "#9A5B00", background: "#FCEBC4" },
    error: { text: "#B42318", background: "#FEE4E2" },
    info: { text: "#2457C5", background: "#E8F0FF" },
  },
};

const t = TH_TOKENS;

/** The guide expressed as the app-wide brand tokens. */
export const thLightBrand: BrandTokens = {
  accent: t.teal,
  accentStrong: t.tealDark,
  onAccent: "#FFFFFF",
  shell: { background: "#FFFFFF", text: t.ink },
  border: t.border,
  wordmark: { lead: t.teal, rest: t.ink },
  gradient: {
    hero: `linear-gradient(120deg, ${t.ink} 0%, ${t.inkHover} 100%)`,
    heroHover: `linear-gradient(120deg, ${t.inkHover} 0%, #1E4A73 100%)`,
    alt: `linear-gradient(120deg, ${t.tealDark} 0%, ${t.teal} 100%)`,
    muted: `linear-gradient(120deg, ${t.slate} 0%, #3B4659 100%)`,
  },
  onHero: "#FFFFFF",
  ambient: "none",
  panel: { neutral: "#F1F4F8", info: t.panelBlue, accent: t.panelMint },
  status: t.status,
  shadow: { card: t.shadow, raised: "0 12px 32px rgb(11 23 54 / 12%)" },
  // Slot 1 is the guide's clinical blue; slots 2-3 keep the CVD-checked reference orange and green.
  chart: [t.clinicalBlue, "#eb6834", "#1baf7a"],
  mediaStage: t.ink,
  map: { land: "#E8EEF5", landStroke: "#D2DCE8", bubble: "rgba(8, 125, 130, 0.5)", bubbleStroke: t.tealDark },
};
const HEADING_FONT = '"Manrope", "Inter", "Helvetica", "Arial", sans-serif';
const TRANSITION = "180ms cubic-bezier(0.4, 0, 0.2, 1)";

// The guide allows one shadow. Low elevations (cards, tables, raised panels) all get it; the high
// ones menus and dialogs use keep MUI's, tinted navy, so an overlay still lifts off the page.
const shadows = lightTheme.shadows.map((s, i) =>
  i === 0 ? s : i <= 4 ? t.shadow : s.replace(/rgba\(0,0,0,/g, "rgba(11,23,54,"),
) as Shadows;

const heading = (rem: number, mobileRem?: number) => ({
  fontFamily: HEADING_FONT,
  fontWeight: 700,
  letterSpacing: "-0.01em",
  lineHeight: 1.25,
  fontSize: `${mobileRem ?? rem}rem`,
  ...(mobileRem && { "@media (min-width:600px)": { fontSize: `${rem}rem` } }),
});

export const thLightTheme = createTheme({
  palette: {
    mode: "light",
    primary: { light: t.inkHover, main: t.ink, dark: "#070F24", contrastText: "#ffffff" },
    secondary: { light: t.brightTeal, main: t.teal, dark: t.tealDark, contrastText: "#ffffff" },
    success: { light: t.status.complete.background, main: t.status.complete.text, dark: "#0E5C3E", contrastText: "#ffffff" },
    warning: { light: t.status.action.background, main: t.status.action.text, dark: "#744400", contrastText: "#ffffff" },
    error: { light: t.status.error.background, main: t.status.error.text, dark: "#8A1B12", contrastText: "#ffffff" },
    info: { light: t.status.info.background, main: t.status.info.text, dark: "#1B4296", contrastText: "#ffffff" },
    // Kept from the shared palette so `color="purple"` buttons still resolve.
    purple: lightTheme.palette.purple,
    selected: { light: t.panelMint, main: "#C3E4E0", dark: t.teal, contrastText: t.tealDark },
    form: {
      label: t.slate,
      border: t.inputBorder,
      readOnly: { text: t.ink, border: t.border, label: t.slate },
    },
    background: { default: t.canvas, paper: "#FFFFFF" },
    text: { primary: t.ink, secondary: t.slate, label: t.slate },
    divider: t.border,
    brand: thLightBrand,
  },
  shape: { borderRadius: 10 },
  shadows,
  typography: {
    ...lightTheme.typography,
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: heading(2.5, 2),
    h2: heading(2.25, 1.875),
    h3: heading(2, 1.75),
    // Page title 32 / 28, section 24 / 22, card 20 (the guide's scale).
    h4: heading(2, 1.75),
    h5: heading(1.5, 1.375),
    h6: heading(1.25),
    body1: { fontSize: "1rem", lineHeight: 1.55 },
    body2: { fontSize: "0.875rem", lineHeight: 1.55 },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
  },
  components: {
    // The label/notch coupling (labelNotch.test.ts) and the error-label rule come from here.
    ...lightTheme.components,
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        ...((typeof lightTheme.components?.MuiCssBaseline?.styleOverrides === "function"
          ? lightTheme.components.MuiCssBaseline.styleOverrides(theme)
          : {}) as object),
        body: { backgroundColor: t.canvas },
        // The guide's focus: a 3px teal outline, 3px clear of the control. MUI marks keyboard focus
        // with .Mui-focusVisible; plain links get the native pseudo-class.
        ".Mui-focusVisible, a:focus-visible": {
          outline: `3px solid ${t.teal}`,
          outlineOffset: 3,
        },
      }),
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: `background-color ${TRANSITION}, border-color ${TRANSITION}, color ${TRANSITION}`,
        },
        sizeMedium: { minHeight: 52, padding: "0 24px" },
        sizeLarge: { minHeight: 52, padding: "0 28px" },
        // Dense rows (tables, toolbars) keep a smaller button; 40px is still a comfortable target.
        sizeSmall: { minHeight: 40, padding: "0 16px" },
        // No fill on outlined buttons: a white fill turned every outlined button drawn on a hero
        // banner (white label, `brand.onHero`) into a blank white box. On a white card the
        // transparent button looks the same as a white one.
        outlined: {
          borderColor: t.inputBorder,
        },
      },
      variants: [
        {
          props: { variant: "contained", color: "primary" },
          style: { "&:hover": { backgroundColor: t.inkHover } },
        },
        {
          props: { variant: "outlined", color: "primary" },
          style: {
            color: t.ink,
            "&:hover": { backgroundColor: alpha(t.ink, 0.04), borderColor: t.ink },
          },
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          border: `1px solid ${t.border}`,
          boxShadow: t.shadow,
        },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 24 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        ...(lightTheme.components?.MuiOutlinedInput?.styleOverrides ?? {}),
        root: {
          borderRadius: 10,
          backgroundColor: "#FFFFFF",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: t.inputBorder },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.ink },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: t.teal, borderWidth: 2 },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: t.status.error.text },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          ...(lightTheme.components?.MuiFormLabel?.styleOverrides?.root as object),
          color: t.slate,
          "&.Mui-focused": { color: t.teal },
          "&.Mui-error.Mui-focused": { color: t.teal },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 9999, fontWeight: 600 } },
    },
    MuiLink: {
      defaultProps: { color: t.teal },
      styleOverrides: {
        root: {
          fontWeight: 500,
          transition: `color ${TRANSITION}`,
          "&:hover": { color: t.tealDark },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: alpha(t.teal, 0.1),
            color: t.tealDark,
            "& .MuiListItemIcon-root": { color: t.teal },
            "&:hover": { backgroundColor: alpha(t.teal, 0.16) },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: t.border },
        head: { color: t.slate, fontWeight: 600 },
      },
    },
  },
});
